import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Brain, PlusCircle, Plus, Sparkles } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OnboardingPanel = ({ onOpenAI, onOpenManual, compact = false }) => {
  const { t } = useI18n();
  const { isDemo } = useAuth();

  const handleProRequest = async () => {
    if (isDemo) return;
    try {
      const resp = await fetch(`${API_URL}/api/auth/request-pro-upgrade`, {
        method: "POST",
        credentials: "include",
      });
      if (resp.ok) {
        toast.success(t("assets.proRequestSent") || "Your PRO upgrade request has been sent!");
      } else {
        toast.error(t("assets.proRequestFailed") || "Failed to send request");
      }
    } catch (err) {
      toast.error(t("assets.proRequestFailed") || "Failed to send request");
    }
  };

  if (compact) {
    return (
      <Card className="border-dashed border-2 border-primary/30 mb-6" data-testid="onboarding-panel">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg">{t("assets.welcomeTitle") || "Welcome to Cableway Finance"}</h3>
              <p className="text-sm text-muted-foreground">{t("assets.welcomeSub") || "Start building your portfolio"}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Button 
                size="sm" 
                variant="outline" 
                className="gap-1 opacity-70"
                onClick={handleProRequest}
                disabled={isDemo}
              >
                <Building2 className="w-3 h-3" /> 
                Bank Connect
                <Badge variant="secondary" className="text-[9px] px-1 py-0 ml-1">PRO</Badge>
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="gap-1"
                onClick={onOpenAI}
                disabled={isDemo}
                data-testid="onboard-ai-btn"
              >
                <Brain className="w-3 h-3" /> AI Assistant
              </Button>
              <Button 
                size="sm" 
                className="gap-1"
                onClick={onOpenManual}
                disabled={isDemo}
                data-testid="onboard-manual-btn"
              >
                <Plus className="w-3 h-3" /> Add Asset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-panel">
      <Card className="border-dashed border-2 border-primary/30">
        <CardContent className="py-10">
          <div className="text-center max-w-lg mx-auto space-y-4">
            <h2 className="text-2xl font-bold tracking-tight">{t("assets.welcomeTitle") || "Welcome to Cableway Finance"}</h2>
            <p className="text-muted-foreground">{t("assets.welcomeSub") || "Start building your portfolio"}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 max-w-3xl mx-auto">
            {/* Option 1: PSD2 Open Banking (PRO) */}
            <Card className="relative overflow-hidden group transition-all opacity-70">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex items-center justify-center gap-1">
                  <h3 className="font-bold text-sm">{t("assets.onboardBank") || "Connect Bank"}</h3>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">PRO</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("assets.onboardBankDescPro") || "Automatically import your accounts via Secure PSD2 Open Banking — stocks, cash, and liabilities."}
                </p>
                <div className="pt-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="gap-1"
                    onClick={handleProRequest}
                    disabled={isDemo}
                    data-testid="switch-to-pro-btn"
                  >
                    <Sparkles className="w-3 h-3" /> {t("assets.switchToPro") || "Switch to PRO"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Option 2: AI Portfolio Assistant */}
            <Card className="group hover:border-primary/50 transition-all cursor-pointer" onClick={() => !isDemo && onOpenAI?.()}>
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-sm">{t("assets.onboardAI") || "AI Assistant"}</h3>
                <p className="text-xs text-muted-foreground">{t("assets.onboardAIDesc") || "Let our AI guide you through adding your assets"}</p>
                <Button size="sm" variant="outline" className="mt-2" disabled={isDemo} data-testid="onboard-ai-btn">
                  <Sparkles className="w-3 h-3 mr-1" /> {t("assets.onboardAIBtn") || "Start AI Assistant"}
                </Button>
              </CardContent>
            </Card>

            {/* Option 3: Manual Add */}
            <Card className="group hover:border-primary/50 transition-all cursor-pointer" onClick={() => !isDemo && onOpenManual?.()}>
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <PlusCircle className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-sm">{t("assets.onboardManual") || "Add Manually"}</h3>
                <p className="text-xs text-muted-foreground">{t("assets.onboardManualDesc") || "Add your assets one by one manually"}</p>
                <Button size="sm" variant="outline" className="mt-2" disabled={isDemo} data-testid="onboard-manual-btn">
                  <Plus className="w-3 h-3 mr-1" /> {t("assets.addAsset") || "Add Asset"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingPanel;
